import { RcFile } from 'antd/lib/upload';
import { AxiosError, AxiosRequestConfig } from 'axios';
import { DTOgetModuleFiltersRequestFunction } from '../components/ModuleFilters/ModuleFilters';
import { IBrands, ICategoryList, IProductsLine, ISpecs } from '../types/category';
import { IProfileType } from '../types/forms';
import { ICompusolucionesCatalogDocumentResponse } from '../types/catalogue.types';
import {
  IDataImg,
  IDataNewProduct,
  IProductDetailId,
  IProviders,
  ISheet,
  IToolToChange,
  TypeCatalogue
} from '../types/products';
import { apiGet, apiGetWithCancelation, apiPatch, apiPost, apiPostFile, apiPut } from './api';

const { REACT_APP_CATALOG, REACT_APP_UPLOAD_IMG } = process.env;

const getCatalogue = async (filters?: string, search?: string): Promise<TypeCatalogue> => {
  const filtersQuery = filters && filters.length > 0 ? `filters=${filters}` : '';
  const nameQuery = search && search?.length > 0 ? `${filtersQuery ? '&' : ''}commercialName=${search}` : '';
  const queries = !!filtersQuery || !!nameQuery ? `?${filtersQuery}${nameQuery}` : '';
  return apiGet<AxiosRequestConfig, TypeCatalogue>(`${REACT_APP_CATALOG}backoffice${queries}`)
    .then(resp => {
      const productList: TypeCatalogue = resp;
      return productList;
    })
    .catch((error: AxiosError) => Promise.reject(error));
};

const getCatalogueList = async ({ filters = '', isActive = true }: { filters: string; isActive: boolean }) => {
  const active = 'active';
  const inactive = 'inactive';
  const renderStatusFilter = isActive ? active : inactive;
  return apiGetWithCancelation<AxiosRequestConfig, TypeCatalogue>(
    `${REACT_APP_CATALOG}backoffice/listing/by-status/${renderStatusFilter}${filters}`
  )
    .then(resp => {
      const productList: TypeCatalogue = resp;
      return productList;
    })
    .catch((error: AxiosError) => Promise.reject(error));
};

const getProductDetail = async (productId?: string | number): Promise<IProductDetailId> => {
  return apiGet<AxiosRequestConfig, IProductDetailId>(`${REACT_APP_CATALOG}backoffice/${productId}`)
    .then(resp => {
      const productList: IProductDetailId = resp;
      return productList;
    })
    .catch((error: AxiosError) => Promise.reject(error));
};

const getCategories = async (): Promise<ICategoryList> => {
  return apiGet<AxiosRequestConfig, ICategoryList>(`${REACT_APP_CATALOG}category`)
    .then(resp => {
      const categoryList: ICategoryList = resp;
      return categoryList;
    })
    .catch((error: AxiosError) => Promise.reject(error));
};

const getBrandsDetail = async (id: string | number): Promise<IBrands> => {
  return apiGet<AxiosRequestConfig, IBrands>(`${REACT_APP_CATALOG}brand?categoryId=${id}`)
    .then(resp => {
      const categoryList: IBrands = resp;
      return categoryList;
    })
    .catch((error: AxiosError) => Promise.reject(error));
};

const getProductLine = async (): Promise<IProductsLine> => {
  return apiGet<AxiosRequestConfig, IProductsLine>(`${REACT_APP_CATALOG}product-position`)
    .then(resp => {
      const categoryList: IProductsLine = resp;
      return categoryList;
    })
    .catch((error: AxiosError) => Promise.reject(error));
};

const getProfileType = async (): Promise<IProfileType> => {
  return apiGet<AxiosRequestConfig, IProfileType>(`${REACT_APP_CATALOG}product-profile/backoffice`)
    .then(resp => {
      const categoryList: IProfileType = resp;
      return categoryList;
    })
    .catch((error: AxiosError) => Promise.reject(error));
};

const getSpecsLists = async (paramName: string): Promise<ISpecs> => {
  return apiGet<AxiosRequestConfig, ISpecs>(`${REACT_APP_CATALOG}params/by-name/${paramName}`)
    .then(resp => {
      const categoryList: ISpecs = resp;
      return categoryList;
    })
    .catch((error: AxiosError) => Promise.reject(error));
};

const editProduct = async (productId: number | string, body: IProductDetailId): Promise<IProductDetailId> => {
  return apiPut<AxiosRequestConfig, IProductDetailId>(`${REACT_APP_CATALOG}backoffice/${productId}`, body)
    .then(resp => {
      const editProduct: IProductDetailId = resp;
      return editProduct;
    })
    .catch((error: AxiosError) => Promise.reject(error));
};

const createNewProduct = async (body: IProductDetailId): Promise<IDataNewProduct> => {
  return apiPost<AxiosRequestConfig, IProductDetailId>(`${REACT_APP_CATALOG}backoffice/product`, body)
    .then(resp => {
      const newProduct: IDataNewProduct = resp;
      return newProduct;
    })
    .catch((error: AxiosError) => Promise.reject(error));
};

const createImage = async (file: string | Blob | RcFile, isSecondary?: boolean): Promise<IDataImg> => {
  return apiPostFile<AxiosRequestConfig, IDataImg>(
    `${REACT_APP_UPLOAD_IMG}image/backoffice/upload?access=CLOUD_FRONT&service=catalog${
      isSecondary ? '&isMultiple=true' : ''
    }`,
    file
  )
    .then(resp => {
      const editProduct: IDataImg = resp;
      return editProduct;
    })
    .catch((error: AxiosError) => Promise.reject(error));
};

const createURLFile = async (file: string | Blob | RcFile): Promise<ISheet> => {
  return apiPostFile<AxiosRequestConfig, ISheet>(
    `${REACT_APP_UPLOAD_IMG}file/backoffice/upload?access=CLOUD_FRONT`,
    file,
    undefined,
    true
  )
    .then(resp => {
      const editProduct: ISheet = resp;
      return editProduct;
    })
    .catch((error: AxiosError) => Promise.reject(error));
};

const getCatalogueFilters = async (): Promise<DTOgetModuleFiltersRequestFunction> => {
  return apiGet(`${REACT_APP_CATALOG}backoffice/catalog/filters`)
    .then(response => Promise.resolve(response as DTOgetModuleFiltersRequestFunction))
    .catch(error => Promise.reject(error));
};

const getAllProviders = async (): Promise<IProviders> => {
  return apiGet(`${REACT_APP_CATALOG}provider/backoffice`)
    .then(response => Promise.resolve(response as IProviders))
    .catch((error: AxiosError) => Promise.reject(error));
};

const activateOrDeactivateProducts = async ({
  isActivate = true,
  body
}: {
  isActivate?: boolean;
  body?: number[];
}): Promise<string[]> => {
  const activateEndPoint = 'backoffice/products/status/active';
  const deactivateEndPoint = 'backoffice/products/status/inactive';
  const renderEndPoint = isActivate ? deactivateEndPoint : activateEndPoint;
  return apiPut<AxiosRequestConfig, number[]>(`${REACT_APP_CATALOG}${renderEndPoint}`, body)
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

const activateOrDeactivateLoadExternalTool = async ({
  isActive = false,
  id
}: {
  isActive?: boolean;
  id?: string | undefined;
}): Promise<string | undefined> => {
  const on = 'on';
  const off = 'off';
  const renderStatus = isActive ? off : on;

  return apiPut<AxiosRequestConfig, string | undefined>(
    `${REACT_APP_CATALOG}backoffice/product/can-be-loaded-status/${renderStatus}/${id}`
  )
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

/**
 * Actualiza el estado en tienda (Prendido / Para cotizar / Apagado) de un producto en un país específico.
 * Endpoint dedicado, separado del PUT general del producto.
 * @param productId Id del producto a actualizar.
 * @param body Datos del cambio: código de país, isActive, isQuoteProduct y, opcionalmente,
 * ignoreActiveQuotes para forzar el cambio aun cuando existan cotizaciones activas.
 * @returns Detalle completo del producto actualizado.
 */
const updateProductCountryStoreStatus = async (
  productId: number,
  body: { countryCode: string; isActive: boolean; isQuoteProduct: boolean; ignoreActiveQuotes?: boolean }
): Promise<IProductDetailId> => {
  return apiPatch<AxiosRequestConfig, typeof body, IProductDetailId>(
    `${REACT_APP_CATALOG}backoffice/${productId}/store-status`,
    body
  )
    .then(resp => resp)
    .catch((error: AxiosError) => Promise.reject(error));
};

/**
 * Genera la planilla de catálogo Compusoluciones y devuelve la URL del documento.
 * @returns URL de la planilla generada.
 */
const getCompusolucionesCatalogDocument = async (): Promise<string> => {
  return apiPut<AxiosRequestConfig, ICompusolucionesCatalogDocumentResponse>(
    `${REACT_APP_CATALOG}backoffice/compusoluciones/catalog-document`
  )
    .then(response => Promise.resolve(response.url))
    .catch((error: AxiosError) => {
      const message = error.response?.data?.message;
      return Promise.reject(error.response?.status === 400 && message ? new SyntaxError(message) : error);
    });
};

const updateTool = async (body: IToolToChange): Promise<IToolToChange> => {
  return apiPut<AxiosRequestConfig, IToolToChange>(
    `${REACT_APP_CATALOG}product-management/logistic-service/change-products`,
    body
  )
    .then(resp => {
      const toolChange: IToolToChange = resp;
      return toolChange;
    })
    .catch((error: AxiosError) => Promise.reject(error));
};

export {
  getCatalogue,
  getProductDetail,
  getCategories,
  getBrandsDetail,
  getProductLine,
  editProduct,
  getProfileType,
  getSpecsLists,
  createNewProduct,
  createImage,
  createURLFile,
  getCatalogueFilters,
  getAllProviders,
  getCatalogueList,
  activateOrDeactivateProducts,
  activateOrDeactivateLoadExternalTool,
  updateProductCountryStoreStatus,
  getCompusolucionesCatalogDocument,
  updateTool
};
